/** Quick category tag for a task while planning: your own categories, a colour dot, "No category". */
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { api } from "../../lib/api";

export interface Cat { id: string; name: string; color: string }

export function useCategories() {
  return useQuery({ queryKey: ["categories"], queryFn: () => api<{ items: Cat[] }>("/api/categories"), staleTime: 5 * 60_000 }).data?.items ?? [];
}

export function CategoryPick({ itemId, value, onChanged, className }: { itemId: string; value: Cat | null | undefined; onChanged?: (c: Cat | null) => void; className?: string }) {
  const cats = useCategories();
  const [cur, setCur] = useState<Cat | null>(value ?? null);
  const [busy, setBusy] = useState(false);
  const set = async (id: string) => {
    const next = cats.find((c) => c.id === id) ?? null;
    const prev = cur;
    setCur(next); setBusy(true);
    try { await api(`/api/work/items/${itemId}/category`, { method: "PUT", json: { categoryId: next?.id ?? null } }); onChanged?.(next); }
    catch { setCur(prev); } finally { setBusy(false); }
  };
  if (!cats.length) return null;
  return (
    <span className={clsx("relative inline-flex min-w-0 items-center", className)}>
      <span aria-hidden className="pointer-events-none absolute left-1.5 size-2 rounded-full" style={{ background: cur?.color ?? "transparent", border: cur ? "none" : "1px dashed var(--muted, #999)" }} />
      <select
        className="input !h-6 !min-h-0 !w-auto min-w-0 max-w-[10rem] !py-0 !pl-5 !pr-6 !text-[11px] !bg-[position:right_0.35rem_center]"
        value={cur?.id ?? ""} disabled={busy} onChange={(e) => void set(e.target.value)} aria-label="Category" title="Category"
      >
        <option value="">No category</option>
        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    </span>
  );
}
